/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { fetchData, joinDataArrays, formatDate } from "./utils/functions";

export default function AnalyticsPage() {
  const statusQuery = useQuery("statusData", () => fetchData("status"));
  const itemStatusQuery = useQuery("itemStatusData", () => fetchData("item-status"));
  const salesChannelQuery = useQuery("salesChannelData", () => fetchData("sales-channel"));
  const itemQuery = useQuery("itemData", () => fetchData("item"));

  const currentDate = new Date();
  const temporaryEndDate = new Date(currentDate);
  temporaryEndDate.setMonth(temporaryEndDate.getMonth() - 1);

  const initialStartDate = formatDate(temporaryEndDate);
  const initialEndDate = formatDate(currentDate);

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [soldStatusId, setSoldStatusId] = useState(null);
  const [soldItemStatuses, setSoldItemStatuses] = useState([]);
  const [groupedSoldItems, setGroupedSoldItems] = useState({});
  const [saleTotals, setSaleTotals] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [shouldUpdate, setShouldUpdate] = useState(false);

  useEffect(() => {
    if (statusQuery.data) {
      const soldStatus = statusQuery.data.find((status) => status.name === "sold");
      if (soldStatus) {
        setSoldStatusId(soldStatus.id);
      }
    }
  }, [statusQuery.data]);

  useEffect(() => {
    if (itemStatusQuery.data && soldStatusId) {
      setSoldItemStatuses(
        itemStatusQuery.data.filter((itemStatus) => itemStatus.status_id === soldStatusId)
      );
    }
  }, [itemStatusQuery.data, soldStatusId]);

  const fetchResults = () => {
    if (!soldItemStatuses.length || !itemQuery.data || !salesChannelQuery.data) return;

    setIsLoading(true);

    setTimeout(() => {
      const addOneDay = (dateString) => {
        const date = new Date(dateString);   // "2025-09-28" → Sun Sep 28 2025 00:00:00
        date.setDate(date.getDate() + 1);    // add one day
        return date;
      };
      const tomorrow = addOneDay(endDate)
      const filteredItemStatuses = soldItemStatuses
        .filter((itemStatus) => {
          const entryDate = new Date(itemStatus.entry_date);
          return entryDate >= new Date(startDate) && entryDate <= new Date(tomorrow);
        })
        .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date));

      const filteredItemStatusesWithItemData = joinDataArrays(
        filteredItemStatuses, 'item_id', itemQuery.data, 'id', {
          description: "description",
          item_entry_date: "entry_date",
          estimated_shipping: "estimated_shipping",
          is_media_mail: "is_media_mail",
          location_id: "location_id",
          minimum_return: "minimum_return",
          sale_price: "sale_price",
          shipping_cost: "shipping_cost",
          source_id: "source_id",
        }
      );

      const groupedItemStatusesWithItemData = filteredItemStatusesWithItemData.reduce((acc, item) => {
        const channelId = item.sales_channel_id;
        if (!acc[channelId]) {
          acc[channelId] = [];
        }
        acc[channelId].push(item);
        return acc;
      }, {});

      let totalProfit = 0;
      Object.entries(groupedItemStatusesWithItemData).forEach(([channelId, items]) => {
        const channelName = salesChannelQuery.data.find((ch) => ch.id === parseInt(channelId))?.channel || "Unknown Channel";
        const isEbay = channelName.toLowerCase() === "ebay";

        items.forEach((soldItemStatus) => {
          const adjustedSalePrice = isEbay ? soldItemStatus.sale_price * 0.85 : soldItemStatus.sale_price;
          totalProfit += adjustedSalePrice - soldItemStatus.minimum_return - soldItemStatus.shipping_cost;
        });
      });

      setGroupedSoldItems(groupedItemStatusesWithItemData);
      setSaleTotals(`Total Time Frame Profits: $${totalProfit.toFixed(2)}`);
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchResults();
  }, [soldItemStatuses, itemQuery.data, salesChannelQuery.data]);

  const handleClick = () => {
    setShouldUpdate(true);
  };

  useEffect(() => {
    if (shouldUpdate) {
      fetchResults();
      setShouldUpdate(false);
    }
  }, [shouldUpdate]);

  return (
    <div>
      <div>
        <label htmlFor="start_date">Start Date:</label>
        <input
          type="date"
          name="start_date"
          id="start_date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="end_date">End Date:</label>
        <input
          type="date"
          name="end_date"
          id="end_date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>
      <button onClick={handleClick} disabled={isLoading}>
        {isLoading ? "Loading..." : "Update Results"}
      </button>

      {isLoading ? (
        <div>Loading sales data...</div>
      ) : Object.keys(groupedSoldItems).length > 0 ? (
        <>
          <div>{saleTotals}</div>
          {Object.entries(groupedSoldItems).map(([channelId, items]) => {
            const channelName =
              salesChannelQuery.data?.find((ch) => ch.id === parseInt(channelId))?.channel || "Unknown Channel";
            return (
              <div key={channelId} style={{ marginBottom: "20px" }}>
                <h3>{channelName}</h3>
                {items.map((soldItemStatus) => {
                  const isEbay = channelName.toLowerCase() === "ebay";
                  const adjustedSalePrice = isEbay ? soldItemStatus.sale_price * 0.85 : soldItemStatus.sale_price;
                  const profit = adjustedSalePrice - soldItemStatus.minimum_return - soldItemStatus.shipping_cost;

                  return (
                    <div key={soldItemStatus.id} style={{ padding: "10px", border: "1px solid #ccc", marginBottom: "10px" }}>
                      <p>Description: {soldItemStatus.description}</p>
                      <p>Minimum Return: ${soldItemStatus.minimum_return}</p>
                      <p>Sale Price: ${soldItemStatus.sale_price}</p>
                      <p>Shipping Cost: ${soldItemStatus.shipping_cost}</p>
                      <p>Profit: ${profit.toFixed(2)}</p>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </>
      ) : (
        <div>No sales occurred during the selected time frame.</div>
      )}
    </div>
  );
}
